import React, { useEffect, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { StaffProfile } from '../../models/staffProfile';
import { ScheduleSession } from './SessionDetailPanel';
import { formatDisplayDate } from '../../services/dateUtils';

interface Props {
  /** All currently-selected sessions (primary + extras). */
  sessions: ScheduleSession[];
  /** YYYY-MM-DD for each session, derived from the displayed week. */
  datesBySessionId: Record<string, string>;
  childProfiles: ChildProfile[];
  staffProfiles: StaffProfile[];
  /** Sessions currently flagged as cancelled for their projected date. */
  cancelledSessionIds: Set<string>;
  /** Triggered when the user applies new teacherIds to all selected. The
   *  returned Promise resolves on success and rejects if any underlying
   *  rule-PATCH failed so the panel can surface a per-result message. */
  onBulkSetTeachers: (sessionIds: string[], teacherIds: number[]) => Promise<void>;
  /** Cancel each selected session for its projected date this week. */
  onBulkCancelForDate: (sessionIds: string[]) => void;
  /** Remove cancellation exceptions for each selected session this week. */
  onBulkRestore: (sessionIds: string[]) => void;
  onClose: () => void;
}

/**
 * Bulk-edit panel — mirrors SessionDetailPanel's outer chrome but only
 * exposes fields that make sense to apply across multiple sessions:
 *   - Teachers (replaces all selected sessions' teacher lists)
 *   - Locations / classrooms
 *   - Cancel / restore for the projected date this week
 *
 * Fields that don't generalise (child, times, day-of-week, recurrence rule)
 * are deliberately omitted. Single-edit mode is always available by clicking
 * a single bar without modifier.
 */
const BulkEditPanel: React.FC<Props> = ({
  sessions, datesBySessionId, childProfiles, staffProfiles,
  cancelledSessionIds,
  onBulkSetTeachers,
  onBulkCancelForDate, onBulkRestore,
  onClose,
}) => {
  // Buffered editor state — applied via the "Apply to all" button so the user
  // can compose a full set of teachers before any writes go through.
  const [teacherIds, setTeacherIds] = useState<number[]>([]);
  const [teachersDirty, setTeachersDirty] = useState(false);
  const [teachersOpen, setTeachersOpen] = useState(false);
  const teachersRef = useRef<HTMLDivElement>(null);

  // Apply-feedback state. Drives the inline "Saving… / Saved ✓ / Failed"
  // pill that appears next to the Apply button.
  type ApplyState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; count: number } | { kind: 'failed'; message: string };
  const [applyState, setApplyState] = useState<ApplyState>({ kind: 'idle' });

  // Reset buffers when the selection changes.
  useEffect(() => {
    setTeacherIds([]);
    setTeachersDirty(false);
    setTeachersOpen(false);
    setApplyState({ kind: 'idle' });
  }, [sessions.map(s => s.id).join(',')]);

  // Auto-clear the saved/failed banner after a short delay.
  useEffect(() => {
    if (applyState.kind === 'saved' || applyState.kind === 'failed') {
      const t = setTimeout(() => setApplyState({ kind: 'idle' }), applyState.kind === 'saved' ? 2500 : 4000);
      return () => clearTimeout(t);
    }
  }, [applyState]);

  // Outside-click + Escape close (mirrors SessionDetailPanel).
  useEffect(() => {
    if (!teachersOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (teachersRef.current && !teachersRef.current.contains(target)) {
        setTeachersOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTeachersOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [teachersOpen]);

  const selectedTeachers = staffProfiles.filter(s => teacherIds.includes(s.id));

  const sessionIds = sessions.map(s => s.id);
  const allCancelled = sessions.every(s => cancelledSessionIds.has(s.id));
  const anyCancellable = sessions.some(s => !cancelledSessionIds.has(s.id));

  const childById = new Map(childProfiles.map(c => [c.id, c]));

  const toggleTeacher = (id: number) => {
    setTeachersDirty(true);
    setTeacherIds(prev =>
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id],
    );
  };

  const handleApplyTeachers = async () => {
    setApplyState({ kind: 'saving' });
    try {
      await onBulkSetTeachers(sessionIds, teacherIds);
      setTeachersDirty(false);
      setApplyState({ kind: 'saved', count: sessionIds.length });
    } catch (err) {
      setApplyState({
        kind: 'failed',
        message: err instanceof Error ? err.message : 'Failed to save',
      });
    }
  };

  return (
    <div className="wsg-sdp">
      <div className="wsg-sdp__header">
        <h6 className="wsg-sdp__title">
          <i className="fas fa-layer-group" style={{ marginRight: 8, color: '#26923f' }} />
          Bulk edit · {sessions.length} sessions
        </h6>
        <button className="wsg-sdp__close" onClick={onClose} title="Close">
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Selection summary */}
      <div className="wsg-sdp__field">
        <span className="wsg-sdp__label">Selected</span>
        <div
          style={{
            maxHeight: 120,
            overflowY: 'auto',
            border: '1px solid rgba(38,146,63,0.18)',
            borderRadius: 6,
            background: 'rgba(255,255,255,0.55)',
            padding: '4px 8px',
            fontSize: '0.78rem',
            color: '#3a4538',
          }}
        >
          {sessions.map(s => {
            const c = s.childId != null ? childById.get(s.childId) : null;
            const name = c ? `${c.first_name} ${c.last_name}` : '—';
            const dateStr = datesBySessionId[s.id];
            const isCancelled = cancelledSessionIds.has(s.id);
            return (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 0',
                  borderBottom: '1px dashed rgba(38,146,63,0.12)',
                  opacity: isCancelled ? 0.6 : 1,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: c?.colour_code || '#aaa',
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                <strong style={{ fontWeight: 600 }}>{name}</strong>
                <span style={{ color: '#7e8a78', marginLeft: 'auto' }}>
                  {dateStr ? formatDisplayDate(dateStr) : ''} · {s.startTime}
                </span>
                {isCancelled && (
                  <i
                    className="fas fa-ban"
                    style={{ color: '#a86a00', marginLeft: 4 }}
                    title="Cancelled for this date"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Teachers — same trigger + dropdown as the single-session editor. */}
      <div className="wsg-sdp__teachers-wrap" ref={teachersRef}>
        <span className="wsg-sdp__label">Teachers</span>
        <p style={{ fontSize: '0.75rem', color: '#7e8a78', margin: '0 0 6px' }}>
          Replaces the teacher list on all selected rules.
        </p>
        <button
          type="button"
          className={`wsg-sdp__teachers-trigger${teachersOpen ? ' is-open' : ''}`}
          onClick={() => setTeachersOpen(v => !v)}
          aria-haspopup="listbox"
          aria-expanded={teachersOpen}
        >
          {selectedTeachers.length === 0 ? (
            <span className="wsg-sdp__teachers-empty">No teachers assigned</span>
          ) : (
            <div className="wsg-sdp__teachers-stack">
              {selectedTeachers.slice(0, 4).map((t, i) => (
                <span
                  key={t.id}
                  className="wsg-sdp__teachers-chip"
                  style={{ background: t.colourCode, zIndex: selectedTeachers.length - i }}
                  title={t.name}
                >
                  {t.initials}
                </span>
              ))}
              {selectedTeachers.length > 4 && (
                <span className="wsg-sdp__teachers-more">+{selectedTeachers.length - 4}</span>
              )}
            </div>
          )}
          <i className={`fas fa-chevron-down wsg-sdp__chevron${teachersOpen ? ' is-open' : ''}`} />
        </button>

        {teachersOpen && (
          <div className="wsg-sdp__dropdown" role="listbox" aria-multiselectable="true">
            {staffProfiles.length === 0 ? (
              <div className="wsg-sdp__empty">No editors available</div>
            ) : (
              staffProfiles.map(s => {
                const selected = teacherIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`wsg-sdp__option${selected ? ' is-selected' : ''}`}
                    onClick={() => toggleTeacher(s.id)}
                  >
                    <span
                      className="wsg-sdp__option-circle"
                      style={{ background: s.colourCode }}
                    >
                      {s.initials}
                    </span>
                    <span className="wsg-sdp__option-name">{s.name}</span>
                    {selected && (
                      <i className="fas fa-check wsg-sdp__option-check" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}

        <button
          type="button"
          className="btn btn-sm btn-primary mt-2"
          disabled={!teachersDirty || applyState.kind === 'saving'}
          onClick={handleApplyTeachers}
          style={{ width: '100%' }}
        >
          {applyState.kind === 'saving'
            ? <><i className="fas fa-spinner fa-spin me-2" />Applying…</>
            : `Apply teachers to ${sessions.length} sessions`}
        </button>

        {applyState.kind === 'saved' && (
          <div
            role="status"
            style={{
              marginTop: 8,
              padding: '7px 12px',
              borderRadius: 8,
              background: 'rgba(124, 179, 66, 0.12)',
              border: '1px solid rgba(124, 179, 66, 0.45)',
              color: '#3a6e1d',
              fontSize: '0.82rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <i className="fas fa-check-circle" style={{ color: '#5a9a30' }} />
            Teachers updated on {applyState.count} {applyState.count === 1 ? 'session' : 'sessions'}.
          </div>
        )}
        {applyState.kind === 'failed' && (
          <div
            role="alert"
            style={{
              marginTop: 8,
              padding: '7px 12px',
              borderRadius: 8,
              background: 'rgba(184, 48, 47, 0.08)',
              border: '1px solid rgba(184, 48, 47, 0.45)',
              color: '#962522',
              fontSize: '0.82rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <i className="fas fa-exclamation-triangle" />
            {applyState.message}
          </div>
        )}
      </div>

      {/* Cancel / restore */}
      <hr className="wsg-sdp__hr" />
      <div className="wsg-sdp__field">
        <span className="wsg-sdp__label">This week's occurrences</span>
        {anyCancellable && (
          <button
            type="button"
            className="btn btn-outline-warning w-100 mt-1 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => onBulkCancelForDate(sessionIds)}
          >
            <i className="fas fa-ban" />
            Cancel selected for this week
          </button>
        )}
        {allCancelled && (
          <button
            type="button"
            className="btn btn-outline-secondary w-100 mt-2 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => onBulkRestore(sessionIds)}
          >
            <i className="fas fa-undo" />
            Restore selected for this week
          </button>
        )}
        {!allCancelled && !anyCancellable && (
          <p style={{ fontSize: '0.78rem', color: '#7e8a78', margin: '6px 0 0' }}>
            Selection is empty.
          </p>
        )}
      </div>
    </div>
  );
};

export default BulkEditPanel;
